import {
  ctaOptionDefs,
  ctaColorOptionDefs,
  CTAOptionButton,
} from "@/pages/cta-options/components/CTAOptionButton";

function StateLegend() {
  const items = [
    { label: "Hover", desc: "Lift + brighten / tint" },
    { label: "Active", desc: "Press down, slight scale" },
    { label: "Focus", desc: "Visible ring (Tab)" },
    { label: "Disabled", desc: "50% opacity, muted" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-600">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-semibold">
            {it.label}
          </span>
          <span>{it.desc}</span>
        </span>
      ))}
    </div>
  );
}

function SampleRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-foreground-400 mb-2">
        {label}
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        {children}
      </div>
    </div>
  );
}

function Panel({
  title,
  theme,
  option,
}: {
  title: string;
  theme: "light" | "dark";
  option: (typeof ctaOptionDefs)[number];
}) {
  const isDark = theme === "dark";
  return (
    <div
      className={`flex-1 rounded-xl p-5 flex flex-col gap-5 ${
        isDark ? "bg-[#0F172A]" : "bg-background-100 border border-background-200/70"
      }`}
    >
      <p className={`text-xs font-semibold ${isDark ? "text-background-500" : "text-foreground-500"}`}>
        {title}
      </p>

      <SampleRow label="Default · desktop">
        <CTAOptionButton option={option} theme={theme} />
      </SampleRow>

      <div
        className={`h-px w-full ${isDark ? "bg-white/10" : "bg-background-200/70"}`}
      />

      <SampleRow label="Full-width · mobile">
        <CTAOptionButton option={option} theme={theme} fullWidth />
      </SampleRow>

      <div
        className={`h-px w-full ${isDark ? "bg-white/10" : "bg-background-200/70"}`}
      />

      <SampleRow label="Disabled">
        <CTAOptionButton option={option} theme={theme} disabled />
      </SampleRow>
    </div>
  );
}

function OptionCard({ option }: { option: (typeof ctaOptionDefs)[number] }) {
  return (
    <div className="rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden">
      <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-background-200/50">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#2563EB] via-[#7C3AED] to-[#F97316] flex items-center justify-center text-white font-bold text-sm font-heading">
            {option.id}
          </span>
          <div>
            <h3 className="text-sm font-bold font-heading text-foreground-950">
              Option {option.id} — {option.name}
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">{option.note}</p>
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium text-secondary-700 bg-secondary-100 px-2 py-1 rounded-full whitespace-nowrap">
          <i className="ri-eye-line text-xs" />
          Hover / focus to preview
        </span>
      </div>

      <div className="p-5 flex flex-col lg:flex-row gap-4">
        <Panel title="Light background" theme="light" option={option} />
        <Panel title="Dark background" theme="dark" option={option} />
      </div>
    </div>
  );
}

export default function CtaOptionsPage() {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-background-50/90 backdrop-blur border-b border-background-200/70">
        <div className="container-astro py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#2563EB] via-[#7C3AED] to-[#F97316] flex items-center justify-center">
                <i className="ri-palette-line text-[11px] text-white" />
              </span>
              <h1 className="text-base font-bold font-heading text-foreground-950">
                Primary CTA · 16 Design Alternatives
              </h1>
            </div>
            <p className="text-xs text-foreground-500 mt-1">
              8 brand-derived + 8 alternative color palettes. Live site untouched — this is a review board only.
            </p>
          </div>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-sm" />
            Back to site
          </a>
        </div>
      </div>

      <div className="container-astro py-8 md:py-10">
        {/* Reference + legend */}
        <div className="mb-8 rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
          <div className="shrink-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-3">
              Current live button
            </p>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover shadow-lg shadow-[#7C3AED]/20"
            >
              Let&apos;s Talk
              <i className="ri-arrow-right-line text-xs btn-arrow" />
            </a>
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-2">
              Shared across every option
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed">
              Label "Let&apos;s Talk" · same padding scale (px-5 py-2.5) · Inter, text-sm
              font-semibold · WCAG AA text contrast · responsive sizing (desktop / tablet /
              mobile) with a full-width mobile variant. Hover, active, focus and disabled
              states are defined and interactive.
            </p>
          </div>
          <StateLegend />
        </div>

        {/* Contrast note */}
        <div className="mb-8 rounded-2xl border border-secondary-200 bg-secondary-50 p-4 md:p-5 flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center shrink-0">
            <i className="ri-contrast-2-line text-base" />
          </span>
          <div className="text-sm text-secondary-900 leading-relaxed">
            <p className="font-semibold mb-1">Accessibility note</p>
            <p>
              Your current orange stop <code className="px-1 py-0.5 bg-secondary-100 rounded">#F97316</code>{" "}
              only reaches ~2.4:1 with white text (fails WCAG AA). To keep the same
              blue → purple → orange identity while meeting AA, the gradient-fill options use an
              AA-safe deepened orange stop <code className="px-1 py-0.5 bg-secondary-100 rounded">#C2410C</code>{" "}
              (~5.2:1 with white). Ghost/gradient-text options use dark-on-light text in light mode
              and light-on-dark text in dark mode for AA contrast.
            </p>
          </div>
        </div>

        {/* Brand Gradient Options (1-8) */}
        <div className="mb-6">
          <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-gradient-to-br from-[#2563EB] via-[#7C3AED] to-[#F97316] flex items-center justify-center text-white text-xs font-bold">1</span>
            Brand Gradient Palette (Options 1–8)
          </h2>
          <p className="text-sm text-foreground-600 mb-6">
            Derived from the AstroJal brand identity: blue → purple → orange gradient. These stay closest to your current visual identity.
          </p>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-12">
          {ctaOptionDefs.map((option) => (
            <OptionCard key={option.id} option={option} />
          ))}
        </div>

        {/* Alternative Color Options (9-16) */}
        <div className="mb-6">
          <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-gradient-to-br from-[#059669] via-[#0891B2] to-[#DB2777] flex items-center justify-center text-white text-xs font-bold">2</span>
            Alternative Color Palettes (Options 9–16)
          </h2>
          <p className="text-sm text-foreground-600 mb-6">
            Completely different color directions for a fresh look. Hover over each to see the interactive states.
          </p>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {ctaColorOptionDefs.map((option) => (
            <OptionCard key={option.id} option={option} />
          ))}
        </div>
      </div>
    </div>
  );
}